import { v4 as uuid } from 'uuid';
import {
  defaultsRetroactiveWaitingPeriodReimbursement,
  getPolicyTypeFromTraditionalLTCQuote,
} from '@shared';
import { isTraditionalLTCQuote } from '@client/util/quoteTypeGuards';
import {
  createPolicyFundingSourceFromQuote,
  parseBioPercentToInflationRate,
} from '@client/util/policyQuoteCalculations';
import type { GuidedPlanningQuote } from '@/features/guided-financial-planning/hooks/use-quotes-display';

interface BuildPolicyFundingSourceParams {
  quote: GuidedPlanningQuote;
  sessionId: string;
  client: Client;
  isAdvisorSession: boolean;
  advisorId?: string;
}

export function buildPolicyFundingSource({
  quote,
  sessionId,
  client,
  isAdvisorSession,
  advisorId,
}: BuildPolicyFundingSourceParams): PendingPolicyFundingSource {
  const fundingSourceId = uuid();

  if (isTraditionalLTCQuote(quote)) {
    const inflationRate = parseBioPercentToInflationRate(quote.bioPercent);
    const premiumCostPerMonth =
      quote.premiumFrequency === 'Monthly'
        ? quote.primaryModalPremium
        : quote.primaryModalPremium / 12;
    const policyType = getPolicyTypeFromTraditionalLTCQuote(quote);

    return {
      hasPolicyFunding: true,
      isSimpleInflationProtection: false,
      addedByAdvisorId: isAdvisorSession ? advisorId : undefined,
      isIndemnityPolicyPayment: false,
      policyBenefitIsUnlimited: false,
      policyMaximumBenefitAmount: quote.benefitPool,
      policyPremiumMonthlyCost: premiumCostPerMonth,
      policyPremiumStartYear: new Date().getFullYear(),
      policyInflationProtection: inflationRate,
      policyLumpSumPayment: null,
      dailyHomeBenefitAmount: quote.dailyBenefitAmount,
      dailyHomeInflationProtectionPercent: null,
      dailyIndependentLivingBenefitAmount: quote.dailyBenefitAmount,
      dailyIndependentLivingInflationProtectionPercent: null,
      dailyAssistedLivingBenefitAmount: quote.dailyBenefitAmount,
      dailyAssistedLivingInflationProtectionPercent: null,
      dailyNursingHomeBenefitAmount: quote.dailyBenefitAmount,
      dailyNursingHomeInflationProtectionPercent: null,
      homeCareWaitingPeriodDays: quote.eliminationPeriod,
      facilityCareWaitingPeriodDays: quote.eliminationPeriod,
      hasRetroactiveWaitingPeriodReimbursement: defaultsRetroactiveWaitingPeriodReimbursement(policyType)
        ? true
        : null,
      policyLimitedPayYears: null,
      policyBenefitPeriodMonths: null,
      policyMonthlyWithdrawalRate: null,
      policyPremiumIncreaseRate: null,
      policyPremiumIncreaseYears: null,
      policyInflationProtectionOnFaceAmount: null,
      policyInflationProtectionOnCOBAmount: null,
      policyInflationProtectionYears: null,
      policyBenefitUtilizedToDate: 0,
      policyContinuationBenefitAmount: null,
      policyMinimumGuaranteedBenefitAmount: null,
      customPolicyId: quote.traditionalLTCQuoteId,
      addedViaShopInsurance: true,
      insuranceQuoteId: quote.traditionalLTCQuoteId,
      sourceQuote: quote,
      policyType,
      policyAddedAt: Date.now(),
      guidedPlanningSessionId: sessionId,
      pendingFundingSourceId: fundingSourceId,
    };
  } else {
    const basePolicy = createPolicyFundingSourceFromQuote(
      quote,
      client,
      isAdvisorSession,
      advisorId,
      quote,
      null,
    );
    return {
      ...basePolicy,
      guidedPlanningSessionId: sessionId,
      pendingFundingSourceId: fundingSourceId,
    };
  }
}
